import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react';
import { type FullGestureState, useDrag } from '@use-gesture/react';
import type { RewardType } from '../../types';
import { getOriginalReward, type WheelRewardType } from './use-wheel-rewards';
import { calculateXYAngle, radToTurn } from './utils';

export interface WheelBehaviourOptions {
  rewards: WheelRewardType[];
  selectedWinningRewardId?: string;
  onSpinStart?: () => void;
  onSpinEnd?: (winningReward: RewardType) => void;
  disabled?: boolean;
  onInteractWhenDisabled?: () => void;
}

export function useWheelBehaviour({
  rewards,
  selectedWinningRewardId,
  onSpinStart,
  onSpinEnd,
  disabled,
  onInteractWhenDisabled
}: WheelBehaviourOptions) {
  // 1 - clockwise, -1 - counter-clockwise.
  const spinDirectionRef = useRef(1);
  const isSpinningRef = useRef(false);
  const winningRewardRef = useRef<WheelRewardType | undefined>(undefined);

  const spindleRef = useRef<HTMLDivElement | null>(null);
  const spin = useCallback(
    function spin() {
      const spindle = getSpindle(spindleRef);
      const isRewardSelected =
        winningRewardRef.current &&
        winningRewardRef.current.type !== 'spinAgain';

      if (!spindle || isSpinningRef.current || isRewardSelected) {
        return;
      }

      if (disabled) {
        callOnInteractWhenDisabled();
        return;
      }

      isSpinningRef.current = true;

      // The spindle is animated, so when the spin is initiated,
      // it could be in any position. We need to determinte the computed turn
      // and fix the spindle in that position before spinning starts.
      // Once it's fixed, we can disable animation.
      const computedTurn = getComputedTurn(spindle);
      applyTurn(computedTurn);
      disableAnimation();
      callOnSpinStart();

      winningRewardRef.current = pickWinningReward(rewards);

      // The additional turn determine the number of full turns
      // the spindle will make before stopping at the winning reward position.
      const additionalTurn = 4;
      const rewardTurn = findRewardTurn(
        rewards,
        winningRewardRef.current,
        spinDirectionRef.current
      );
      const targetTurn =
        (additionalTurn + rewardTurn) * spinDirectionRef.current;

      requestAnimationFrame(() => {
        enableTransition();
        // In order to change the turn with transition,
        // we need to schedule the change in the next render cycle.
        requestAnimationFrame(() => {
          applyTurn(targetTurn);
        });
      });
    },
    [rewards, disabled]
  );

  const handleTransitionEnd = useCallback(function handleTransitionEnd() {
    if (isSpinningRef.current) {
      isSpinningRef.current = false;
      disableTransition();
      callOnSpinEnd();
    }
  }, []);

  const reset = useCallback(
    function reset() {
      spinDirectionRef.current = 1;
      isSpinningRef.current = false;
      disableAnimation();
      disableTransition();
      resetTransitionTimingFunction();

      // When the selected winning reward is set,
      // we want to recreate the state as if it's been picked by spinning,
      // instead of resetting the wheel to the default state.
      const selectedReward = selectedWinningRewardId
        ? findReward(rewards, selectedWinningRewardId)
        : undefined;
      if (selectedReward) {
        const winningRewardHasChanged =
          !winningRewardRef.current ||
          selectedReward !== getOriginalReward(winningRewardRef.current);
        if (winningRewardHasChanged) {
          winningRewardRef.current = selectedReward;
          const targetTurn = findRewardTurn(
            rewards,
            selectedReward,
            spinDirectionRef.current,
            0
          );
          applyTurn(targetTurn);
        }

        return;
      }

      winningRewardRef.current = undefined;
      resetTurn();
      requestAnimationFrame(enableAnimation);
    },
    [rewards, selectedWinningRewardId]
  );

  // Manage turn.

  const applyTurn = useCallback(function applyTurn(turn: number) {
    const spindle = getSpindle(spindleRef);
    spindle?.style.setProperty('transform', `rotate(${turn}turn)`);
  }, []);

  const resetTurn = useCallback(function applyTurn() {
    const spindle = getSpindle(spindleRef);
    spindle?.style.removeProperty('transform');
  }, []);

  // Transition and animation.
  //
  // It's expected that the spindle has defined transition and animation properties.
  // So to disable transition or animation, we set the property to 'none' inline styles.
  // And to enable, we simply remove the property.

  const enableTransition = useCallback(function enableTransition() {
    const spindle = getSpindle(spindleRef);
    spindle?.style.removeProperty('transition-duration');
  }, []);

  const disableTransition = useCallback(function disableTransition() {
    const spindle = getSpindle(spindleRef);
    spindle?.style.setProperty('transition-duration', '0s');
  }, []);

  const applyTransitionTimingFunction = useCallback(
    function applyTransitionTimingFunction(timingFunction: string) {
      const spindle = getSpindle(spindleRef);
      spindle?.style.setProperty('transition-timing-function', timingFunction);
    },
    []
  );

  const resetTransitionTimingFunction = useCallback(
    function resetTransitionTimingFunction() {
      const spindle = getSpindle(spindleRef);
      spindle?.style.removeProperty('transition-timing-function');
    },
    []
  );

  const enableAnimation = useCallback(function enableAnimation() {
    const spindle = getSpindle(spindleRef);
    spindle?.style.removeProperty('animation');
  }, []);

  const disableAnimation = useCallback(function disableAnimation() {
    const spindle = getSpindle(spindleRef);
    spindle?.style.setProperty('animation', 'none');
  }, []);

  // Touch control.

  const dragInitialTurn = useRef(0);
  const buildDragRootAttributes = useDrag((drag) => {
    drag.event.preventDefault();

    const spindle = getSpindle(spindleRef);

    if (!spindle || isSpinningRef.current || winningRewardRef.current) {
      return;
    }

    if (disabled) {
      callOnInteractWhenDisabled();
      return;
    }

    const dragDeltaReleaseThreshold = 0.4;
    const velocityThreshold = 0.2;

    function capture() {
      dragInitialTurn.current = getComputedTurn(spindle);
      applyTurn(dragInitialTurn.current);
      disableAnimation();
    }

    function move() {
      const delta = calculateDragTurnDelta(spindle!, drag);
      if (Math.abs(delta) > dragDeltaReleaseThreshold) {
        // When the delta reaches the threshold, we force the drag release.
        release(true);
      } else {
        const smoothDelta = delta / 4;
        applyTurn(dragInitialTurn.current + smoothDelta);
      }
    }

    function release(ignoreMomentum = false) {
      const delta = calculateDragTurnDelta(spindle!, drag);
      // Momentum defines whether the wheel was pushed
      // or pulled and released.
      const velocity = Math.sqrt(drag.velocity[0] ** 2 + drag.velocity[1] ** 2);
      const hasMomentum = !ignoreMomentum && velocity > velocityThreshold;

      if (hasMomentum) {
        // When it's pushed we want the wheel to spin in the push direction.
        spinDirectionRef.current = delta > 0 ? 1 : -1;

        // Apply custom transition timing function to mimic the inertia.
        applyTransitionTimingFunction('cubic-bezier(.35,.93,.22,1.01)');
      } else {
        // When it's gently pulled and released, we want the wheel to spin
        // in an opposite direction, mimicking a rotary dial phone.
        spinDirectionRef.current = delta > 0 ? -1 : 1;
      }

      spin();
    }

    switch (drag.type) {
      case 'pointerdown':
        capture();
        break;

      case 'pointermove':
        move();
        break;

      case 'lostpointercapture':
        release();
        break;

      case 'pointerup':
        release();
        break;
    }
  });

  // Handle the callbacks.

  const onSpinStartRef = useRef(onSpinStart);
  onSpinStartRef.current = onSpinStart;
  const callOnSpinStart = useCallback(function callOnSpinStart() {
    onSpinStartRef.current?.();
  }, []);

  const onSpinEndRef = useRef(onSpinEnd);
  onSpinEndRef.current = onSpinEnd;
  const callOnSpinEnd = useCallback(function callOnSpinEnd() {
    if (!winningRewardRef.current) {
      throw new Error('The winning reward is not set.');
    }
    onSpinEndRef.current?.(getOriginalReward(winningRewardRef.current));
  }, []);

  const onInteractWhenDisabledRef = useRef(onInteractWhenDisabled);
  onInteractWhenDisabledRef.current = onInteractWhenDisabled;
  const callOnInteractWhenDisabled = useCallback(
    function callOnInteractWhenDisabled() {
      onInteractWhenDisabledRef.current?.();
    },
    []
  );

  // Reset the wheel state when the rewards or a selected winning reward change.
  // The useLayoutEffect is used because we want to control the spindle manually,
  // thereby we need an element which is available only after it's mounted in the DOM.
  useLayoutEffect(reset, [reset, rewards, selectedWinningRewardId]);

  return {
    spindleRef,
    buildDragRootAttributes,
    spin,
    reset,
    handleTransitionEnd
  };
}

function getSpindle(spindleRef: RefObject<HTMLDivElement | null>) {
  if (!spindleRef.current && process.env.NODE_ENV === 'development') {
    console.warn(
      `The spindle element is requested though it is not mounted yet. It's not necessarily a bug but may be the expected consequence of the double rendering.`
    );
  }
  return spindleRef.current;
}

export function getComputedTurn(element: HTMLElement | null): number {
  if (!element) return 0;
  const transform = getComputedStyle(element).getPropertyValue('transform');

  if (transform === 'none') {
    return 0;
  }

  // We expext no 3D transformations so, if the computed
  // transform isn't 'none', it is always a matrix(a, b, c, d, e, f).
  const values = transform
    .substring(7, transform.length - 1)
    .split(',')
    .map((value) => parseFloat(value.trim()));
  if (values.length !== 6) {
    throw new Error('Failed to parse a transform matrix.');
  }

  const [cosTheta, sinTheta] = values;
  return radToTurn(calculateXYAngle(cosTheta, sinTheta));
}

function pickWinningReward(rewards: WheelRewardType[]) {
  if (rewards.length === 0) {
    throw new Error('The rewards is expected to have at least one element.');
  }

  // Use the weighted random selection algorithm.

  const totalChance = rewards.reduce(
    (result, entry) => result + entry.chance,
    0
  );

  const randomThreshold = Math.random() * totalChance;

  let traversed = 0;
  for (const entry of rewards) {
    traversed += entry.chance;
    if (traversed >= randomThreshold) {
      return entry;
    }
  }

  throw new Error('Picking a winning reward has failed.');
}

function findRewardTurn(
  rewards: WheelRewardType[],
  winningReward: WheelRewardType,
  direction: number,
  maxSwing = 0.4
) {
  const position = rewards.indexOf(winningReward);
  if (position === -1) {
    throw new Error('The winning reward is not in the rewards.');
  }

  // Swing defines the random deviation from the center of the sector.
  const swing = (-maxSwing + Math.random() * maxSwing * 2) * 0.5;

  const clockwiseTurn = 1 - (position + 0.5 + swing) / rewards.length;
  if (direction === -1) {
    // Counter-clockwise.
    return 1 - clockwiseTurn;
  }
  return clockwiseTurn;
}

function findReward(rewards: WheelRewardType[], id: string) {
  return rewards.find((reward) => getOriginalReward(reward).id === id);
}

function calculateDragTurnDelta(
  spindle: HTMLElement,
  drag: FullGestureState<'drag'>
) {
  const spindleRect = spindle.getBoundingClientRect();

  const originX = spindleRect.x + spindleRect.width / 2;
  const originY = spindleRect.y + spindleRect.height / 2;

  const initialX = drag.initial[0] - originX;
  const initialY = originY - drag.initial[1];
  const initialAngle = calculateXYAngle(initialX, initialY);

  const currentX = drag.xy[0] - originX;
  const currentY = originY - drag.xy[1];
  const currentAngle = calculateXYAngle(currentX, currentY);

  const delta = -Math.atan2(
    Math.sin(currentAngle - initialAngle),
    Math.cos(currentAngle - initialAngle)
  );

  return radToTurn(delta);
}
